/**
 * ResizablePanel Component - Individual Panel Resizing
 * 
 * This component makes individual panels (like Initiative Tracker, Actions, etc.)
 * resizable by the user. Each panel can be resized vertically within the sidebar.
 */

import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import './ResizablePanel.css';

const ResizablePanel = ({ 
  title,
  icon,
  headerAction = null,
  children,
  defaultHeight = 300,
  minHeight = 150,
  maxHeight = 600,
  defaultExpanded = true,
  // @date_of_change: 2025-09-12 - Programmatic expand support for icon-mode navigation
  // When this value changes, the panel will expand and scroll into view.
  expandSignal
}) => {
  const [height, setHeight] = useState(defaultHeight);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isResizing, setIsResizing] = useState(false);
  const panelRef = useRef(null);
  const contentId = useId();
  const startY = useRef(0);
  const startHeight = useRef(0);

  // @date_of_change: 2025-09-12 - Expand on external signal and scroll into view
  useEffect(() => {
    if (expandSignal !== undefined) {
      try {
        setIsExpanded(true);
        if (panelRef.current && typeof panelRef.current.scrollIntoView === 'function') {
          panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      } catch (e) {
        // no-op: best-effort
      }
    }
  }, [expandSignal]);

  // Handle mouse down on resize handle
  const handleMouseDown = useCallback((e) => {
    if (!isExpanded) return;
    
    setIsResizing(true);
    startY.current = e.clientY;
    startHeight.current = height;
    
    // Prevent text selection during resize
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ns-resize';
  }, [height, isExpanded]);

  // Handle mouse move during resize
  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;

    const deltaY = e.clientY - startY.current;
    const newHeight = Math.max(minHeight, Math.min(maxHeight, startHeight.current + deltaY));
    
    setHeight(newHeight);
  }, [isResizing, minHeight, maxHeight]);

  // Handle mouse up to end resize
  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, []);

  // Add/remove event listeners for resize
  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Toggle expand/collapse
  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const panelStyle = {
    minHeight: isExpanded ? `${height}px` : 'auto',
    height: isExpanded ? 'auto' : 'auto',
    maxHeight: isExpanded ? 'none' : 'auto',
    transition: isResizing ? 'none' : 'height 0.3s ease',
  };

  return (
    <div 
      ref={panelRef}
      className={`resizable-panel ${isExpanded ? 'expanded' : 'collapsed'}`}
      style={panelStyle}
    >
      {/* Panel Header */}
      <div
        className="panel-header"
        onClick={toggleExpanded}
        data-panel-title={title}
      >
        <div className="panel-heading-group">
          <h3 className="panel-title">
            {icon && <span className="panel-icon" aria-hidden="true">{icon}</span>}
            <span className="panel-text">{title}</span>
          </h3>
          {headerAction && (
            <div
              className="panel-header-action"
              onClick={(event) => event.stopPropagation()}
            >
              {headerAction}
            </div>
          )}
        </div>
        <button
          type="button"
          className="panel-toggle"
          onClick={(event) => {
            event.stopPropagation();
            toggleExpanded();
          }}
          aria-expanded={isExpanded}
          aria-controls={contentId}
          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${title}`}
        >
          {isExpanded ? <ChevronDown size={15} strokeWidth={1.8} /> : <ChevronRight size={15} strokeWidth={1.8} />}
        </button>
      </div>

      {/* Panel Content */}
      {isExpanded && (
        <>
          <div className="panel-content" id={contentId}>
            {children}
          </div>
          
          {/* Resize Handle */}
          <div 
            className="panel-resize-handle"
            onMouseDown={handleMouseDown}
            style={{ cursor: 'ns-resize' }}
          />
        </>
      )}
    </div>
  );
};

export default ResizablePanel;
